import {
  CSSProperties,
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { FLOATING_VIDEO_Z_INDEX } from '../../libs/overlayZIndex';
import { PRIMIS_VIDEO_ADS_CONFIG } from '../../../gameConfig/ads';
import type { TPrimisVideoAdsConfig } from '../../../gameConfig/ads';

// Tag ids arrive on siteConfig.primisConfig (environment overrides applied server-side); the code defaults apply when absent.
export type TPrimisOutstreamConfig = TPrimisVideoAdsConfig;

export const PRIMIS_OUTSTREAM_CONTAINER_ID = 'primis-player-container';
export const PRIMIS_LIVE_VIEW_URL = 'https://live.primis.tech/live/liveView.php';

export function buildPrimisScriptSrc(playerId: string, params: Record<string, string | number> = {}) {
  const query = Object.entries(params)
    .map(([key, value]) => `&${key}=${value}`)
    .join('');
  return `${PRIMIS_LIVE_VIEW_URL}?s=${playerId}${query}`;
}

export function buildPrimisFloatingFallbackScriptSrc(playerId: string = PRIMIS_VIDEO_ADS_CONFIG.playerId) {
  return buildPrimisScriptSrc(playerId, { videoType: 'sticky' });
}

// The mobile tag is served exactly as Primis issues it; its behaviour is configured on their side.
export function buildPrimisMobilePlayerScriptSrc(mobilePlayerId: string) {
  return buildPrimisScriptSrc(mobilePlayerId);
}

export function buildPrimisDockedPlayerScriptSrc(
  width: number,
  height: number,
  dockedPlayerId: string = PRIMIS_VIDEO_ADS_CONFIG.dockedPlayerId
) {
  return buildPrimisScriptSrc(dockedPlayerId, { videoType: 'flow', flowMode: 'both', x: width, y: height });
}

export const PRIMIS_PLAYER_SCRIPT_SRC = buildPrimisScriptSrc(PRIMIS_VIDEO_ADS_CONFIG.playerId);
export const PRIMIS_FLOATING_FALLBACK_SCRIPT_SRC = buildPrimisFloatingFallbackScriptSrc();
export const PRIMIS_VENDOR_PLAYER_CONTAINER_ID = 'primisPlayerContainerDiv';
export const PRIMIS_MOBILE_MEDIA_QUERY = '(max-width: 767px)';
// A docked slot registers within about a second of its route committing; the sticky fallback must not race it.
export const PRIMIS_FLOATING_FALLBACK_SETTLE_MS = 1000;
// The sticky script keeps building its player after its tag is removed; strays outside docked slots are swept this long.
export const PRIMIS_ORPHAN_SWEEP_WINDOW_MS = 5000;

const primisOutstreamContainerStyle: CSSProperties = {
  height: 0,
  minHeight: 0,
  lineHeight: 0,
  fontSize: 0,
  overflow: 'visible'
};

// Primis inserts this body-level wrapper around its floating player. Constrain the
// wrapper's stacking context so vendor-owned descendants cannot cover site chrome.
const primisFloatingLayerStackingStyle = `
body > div:has(> #${PRIMIS_VENDOR_PLAYER_CONTAINER_ID}) {
  position: relative;
  z-index: ${FLOATING_VIDEO_Z_INDEX} !important;
}
`;

// Sizes and places the desktop sticky player when it runs on a mobile viewport. The dedicated
// mobile tag is laid out by Primis, so it gets the stacking rule only.
// Resize the vendor's fixed mobile player directly: transforming its ancestor makes
// position: fixed resolve against the document and strands the player below the viewport.
const primisStickyMobileOverrideStyle = `
@media (max-width: 767px) {
  #${PRIMIS_VENDOR_PLAYER_CONTAINER_ID} [id^="primis_playerSekindoSPlayer"] {
    right: 5px !important;
    bottom: 105px !important;
    left: auto !important;
    top: auto !important;
  }

  #${PRIMIS_VENDOR_PLAYER_CONTAINER_ID} [id^="primis_playerSekindoSPlayer"],
  #${PRIMIS_VENDOR_PLAYER_CONTAINER_ID} #flowCover,
  #${PRIMIS_VENDOR_PLAYER_CONTAINER_ID} [id^="Player-Div-SekindoSPlayer"],
  #${PRIMIS_VENDOR_PLAYER_CONTAINER_ID} [id^="Video-Div-SekindoSPlayer"],
  #${PRIMIS_VENDOR_PLAYER_CONTAINER_ID} [id^="Video-iFrame-SekindoSPlayer"] {
    width: 180px !important;
    height: 101.25px !important;
  }
}
`;
const primisFloatingLayerStyle = primisFloatingLayerStackingStyle + primisStickyMobileOverrideStyle;

const usePrimisRegistrationEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;
const primisPlayerArtifactSelector = [
  `#${PRIMIS_VENDOR_PLAYER_CONTAINER_ID}`,
  '[id^="primis_container_div_"]',
  '[id^="primis_playerSekindoSPlayer"]',
  '[id^="Player-Div-SekindoSPlayer"]',
  '[id^="Video-Div-SekindoSPlayer"]',
  '[id^="Video-iFrame-SekindoSPlayer"]',
  '#contextMenuPrimis',
  '#contextMenusPrimisLogo',
  '#primisLogoWrapper',
  '#primisLogo',
  '#primisLogoTxt',
  '#primisVastIconsContainer',
  'script[src*="live.primis.tech/live/liveView.php"]'
].join(',');

type TPrimisPlacementContext = {
  dockedPlacementCount: number;
  placementMode: 'desktop' | 'mobile' | null;
  registerDockedPlacement: () => { unregister: () => void };
};

const PrimisPlacementContext = createContext<TPrimisPlacementContext>({
  dockedPlacementCount: 0,
  placementMode: null,
  registerDockedPlacement: () => ({
    unregister: () => {
      /* nop */
    }
  })
});

function createPrimisScript(src: string) {
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.src = src;
  script.setAttribute('language', 'javascript');
  return script;
}

// The live sticky player also builds outside any docked slot, so sweeps must stand down while one is mounted.
let mountedStickyFallbackCount = 0;

function removePrimisPlayerArtifactsOutsideDockedSlots() {
  // The sweep window outlives a page or test document; nothing to do once it is gone.
  if (mountedStickyFallbackCount > 0 || typeof document === 'undefined' || !document.body) {
    return;
  }

  document.querySelectorAll(primisPlayerArtifactSelector).forEach((element) => {
    if (element.closest('[data-primis-docked-slot="true"]')) {
      return;
    }

    element.remove();
  });
}

function sweepPrimisOrphansDuring(windowMs: number) {
  removePrimisPlayerArtifactsOutsideDockedSlots();

  const observer = new MutationObserver(removePrimisPlayerArtifactsOutsideDockedSlots);
  observer.observe(document.body, { childList: true, subtree: true });
  window.setTimeout(() => {
    observer.disconnect();
    removePrimisPlayerArtifactsOutsideDockedSlots();
  }, windowMs);
}

type TPrimisPlayerTagProps = {
  id?: string;
  scriptSrc: string;
  style?: CSSProperties;
  sweepOrphansAfterUnmount?: boolean;
};

const PrimisPlayerTag: FC<TPrimisPlayerTagProps> = ({ id, scriptSrc, style, sweepOrphansAfterUnmount = false }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const script = createPrimisScript(scriptSrc);
    container.appendChild(script);
    if (sweepOrphansAfterUnmount) {
      mountedStickyFallbackCount++;
    }

    return () => {
      script.remove();
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }
      if (sweepOrphansAfterUnmount) {
        mountedStickyFallbackCount--;
        sweepPrimisOrphansDuring(PRIMIS_ORPHAN_SWEEP_WINDOW_MS);
        return;
      }
      removePrimisPlayerArtifactsOutsideDockedSlots();
    };
  }, [scriptSrc, sweepOrphansAfterUnmount]);

  return <div id={id} ref={containerRef} style={style} />;
};

export const PrimisPlacementProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [dockedPlacementCount, setDockedPlacementCount] = useState(0);
  const [placementMode, setPlacementMode] = useState<'desktop' | 'mobile' | null>(null);

  usePrimisRegistrationEffect(() => {
    setPlacementMode(window.matchMedia(PRIMIS_MOBILE_MEDIA_QUERY).matches ? 'mobile' : 'desktop');
  }, []);

  const registerDockedPlacement = useCallback(() => {
    let isRegistered = true;
    setDockedPlacementCount((count) => count + 1);

    return {
      unregister: () => {
        if (!isRegistered) {
          return;
        }

        isRegistered = false;
        setDockedPlacementCount((count) => count - 1);
      }
    };
  }, []);
  const value = useMemo(
    () => ({
      dockedPlacementCount,
      placementMode,
      registerDockedPlacement
    }),
    [dockedPlacementCount, placementMode, registerDockedPlacement]
  );

  return <PrimisPlacementContext.Provider value={value}>{children}</PrimisPlacementContext.Provider>;
};

export function usePrimisPlacementMode() {
  return useContext(PrimisPlacementContext).placementMode;
}

export function usePrimisDockedPlacementRegistration(enabled: boolean) {
  const { registerDockedPlacement } = useContext(PrimisPlacementContext);

  usePrimisRegistrationEffect(() => {
    if (!enabled) {
      return;
    }

    const registration = registerDockedPlacement();

    return () => {
      registration.unregister();
    };
  }, [enabled, registerDockedPlacement]);
}

export const PrimisDockedPlayer: FC<{ config?: TPrimisOutstreamConfig; width: number; height: number }> = ({
  config,
  width,
  height
}) => (
  <PrimisPlayerTag
    scriptSrc={buildPrimisDockedPlayerScriptSrc(
      width,
      height,
      config?.dockedPlayerId || PRIMIS_VIDEO_ADS_CONFIG.dockedPlayerId
    )}
  />
);

export const PrimisFloatingFallback: FC<{
  config?: TPrimisOutstreamConfig;
  suppressed?: boolean;
  settleMs?: number;
}> = ({ config, suppressed = false, settleMs = PRIMIS_FLOATING_FALLBACK_SETTLE_MS }) => {
  const { dockedPlacementCount, placementMode } = useContext(PrimisPlacementContext);
  const isEligible = Boolean(placementMode) && !suppressed && dockedPlacementCount === 0;
  // On mobile viewports a configured mobile tag replaces the sticky player; otherwise the sticky player serves everywhere.
  const mobilePlayerId = placementMode === 'mobile' ? config?.mobilePlayerId || '' : '';
  const scriptSrc = mobilePlayerId
    ? buildPrimisMobilePlayerScriptSrc(mobilePlayerId)
    : buildPrimisFloatingFallbackScriptSrc(config?.playerId || PRIMIS_VIDEO_ADS_CONFIG.playerId);
  const [isSettled, setIsSettled] = useState(false);

  // Mount only once no docked slot has been registered for a whole settle window, so a slot that
  // arrives with the route content never has to evict a sticky player that already started.
  useEffect(() => {
    if (!isEligible) {
      setIsSettled(false);
      return;
    }

    const settleTimeout = window.setTimeout(() => {
      setIsSettled(true);
    }, settleMs);

    return () => {
      window.clearTimeout(settleTimeout);
    };
  }, [isEligible, settleMs]);

  if (!isEligible || !isSettled) {
    return null;
  }

  return (
    <>
      <style data-rio-floating-video-layer="primis">
        {mobilePlayerId ? primisFloatingLayerStackingStyle : primisFloatingLayerStyle}
      </style>
      <PrimisPlayerTag
        id={PRIMIS_OUTSTREAM_CONTAINER_ID}
        scriptSrc={scriptSrc}
        style={primisOutstreamContainerStyle}
        sweepOrphansAfterUnmount={true}
      />
    </>
  );
};

export const PrimisOutstream: FC<{ config?: TPrimisOutstreamConfig }> = ({ config }) => (
  <PrimisPlayerTag
    id={PRIMIS_OUTSTREAM_CONTAINER_ID}
    scriptSrc={buildPrimisScriptSrc(config?.playerId || PRIMIS_VIDEO_ADS_CONFIG.playerId)}
    style={primisOutstreamContainerStyle}
  />
);
